// src/components/trial/lp/trial-beta/sections/before-after.tsx
// Ported from the design attachment, markup lines 3602-3685. The drag-behaviour and live-metrics
// script lives in anim-p3.js (4692-4996) — see ../animation/before-after for the split rationale
// and for what that module deliberately does not port (the chart's own line redraw, the entry
// reveal choreography).
//
// One structural departure from a line-for-line port, made necessary by moving the divider from
// imperative DOM writes into React state:
//
//   * the source drives the divider with `handle.style.left = '0'` plus a per-frame
//     `transform: translateX(px)` (source 4726-4727, 5015) — a micro-optimisation that avoids a
//     browser reflow on every drag sample. `position` here is React state, so every drag sample
//     already re-renders; there is nothing left to save by tracking pixels instead of the
//     percentage the state already holds, so the handle just uses `left: <position>%` (the
//     source's OWN pre-script markup, 3674) with the same `translateX(-50%)` centring. The visual
//     result — the divider's centre sits exactly on the clip boundary — is identical.
//
// The keyboard step is 2% (this task's brief), not the source's 8% (source 4990) — the source's
// handle had no `role`/`tabindex` at all (added by the script, 4711-4717); reachable-by-keyboard
// is the deliberate improvement this task makes over the attachment, and 2% is what that
// improvement specifies. Settle-glide-after-release and the "hide pill on first drag, fade it
// back after 4s idle" (source 4990-5010) are also not ported: neither changes what the divider
// does, only how it eases into rest, and the pill stays exactly as authored — always visible.
//
// The mobile fallback card (`.np3-mobile-ba`, built by the script at 4716-4719 because the
// compare card's SVG clip-path trick isn't legible at that width — trial-beta.css already hides
// `[data-anim='ba-slider']` and shows `.np3-mobile-ba` under 720px, Peça "P3" rules 1310-1318)
// reuses this section's own static line data rather than the mobile line's dynamic 0.4-scaled
// window (source 4820-4821, itself built off the redraw this port skips): same points, same
// `Math.max(4, Math.min(104, y))` clamp the source applies, just against the resting curve instead
// of a live one.
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import { cn } from '@/utils/cn';

import { beforeAfterAnimation } from '../animation/before-after';
import { HUD_STRINGS_EN } from '../animation/strings';
import { useSectionAnimation } from '../animation/use-section-animation';

const MIN_POSITION = 0;
const MAX_POSITION = 100;
const KEYBOARD_STEP_PERCENT = 2;

function clampPosition(value: number): number {
  return Math.min(MAX_POSITION, Math.max(MIN_POSITION, value));
}

// Source 3614-3623, verbatim. `ba-charts` (anim-p3.js 4826-4841) would otherwise redraw these
// every 100ms from a seeded window — out of scope here, see ../animation/before-after's header —
// so this stays the source's own static resting frame.
const BAD_LINE_POINTS = '0,55.8 10.1,22.5 20.3,31.1 30.4,30.6 40.5,36.8 50.6,32.7 60.8,47.8 70.9,45.5 81,9.2 91.1,36.7 101.3,40.2 111.4,51.7 121.5,53.6 131.6,80.8 141.8,86.6 151.9,83.3 162,58 172.2,64.4 182.3,59.9 192.4,29.9 202.5,32 212.7,66.7 222.8,61.2 232.9,39.8 243,34.6 253.2,24.3 263.3,22.9 273.4,10.4 283.5,42.7 293.7,53.6 303.8,29.3 313.9,23.5 324.1,53.7 334.2,53.3 344.3,54.8 354.4,70.9 364.6,82.9 374.7,89.1 384.8,62.4 394.9,74.6 405.1,60.1 415.2,22.3 425.3,11 435.4,46.9 445.6,50.8 455.7,51.2 465.8,45.7 475.9,35.9 486.1,27.2 496.2,9.2 506.3,32.6 516.5,46.9 526.6,62.2 536.7,39.7 546.8,74.7 557,70.6 567.1,60.4 577.2,63.9 587.3,68.4 597.5,76 607.6,52 617.7,79.4 627.8,68.8 638,53 648.1,9.2 658.2,30.4 668.4,31.3 678.5,28.6 688.6,36.1 698.7,47.8 708.9,44.7 719,9.2 729.1,36.7 739.2,41.6 749.4,54.2 759.5,40.8 769.6,82.4 779.7,86.7 789.9,68 800,67.7';
const GOOD_LINE_POINTS = '0,62.8 10.1,55.7 20.3,54.1 30.4,56.2 40.5,59.4 50.6,61.5 60.8,62.4 70.9,63.4 81,66.7 91.1,73.7 101.3,84.2 111.4,95.6 121.5,101.3 131.6,101.3 141.8,99.6 151.9,85.9 162,68.6 172.2,52.9 182.3,46.3 192.4,46.3 202.5,47.8 212.7,58.5 222.8,69.8 232.9,78.4 243,83.1 253.2,84.8 263.3,85.5 273.4,87 283.5,89.9 293.7,92.8 303.8,93.1 313.9,88.4 324.1,78.1 334.2,64 344.3,49.9 354.4,46.3 364.6,46.3 374.7,46.3 384.8,60.6 394.9,77.3 405.1,91.5 415.2,99.6 425.3,100.5 435.4,95.6 445.6,88.3 455.7,81.4 465.8,76.7 475.9,74 486.1,71.6 496.2,67.5 506.3,61.1 516.5,53.6 526.6,47.8 536.7,47.1 546.8,53.4 557,66.3 567.1,82.8 577.2,98.1 587.3,101.3 597.5,101.3 607.6,100.6 617.7,87.2 627.8,72.6 638,61.2 648.1,55.1 658.2,54.3 668.4,56.8 678.5,59.8 688.6,61.8 698.7,62.5 708.9,63.7 719,67.6 729.1,75.3 739.2,86.1 749.4,97.4 759.5,101.3 769.6,101.3 779.7,97.7 789.9,83.1 800,65.7';
// Source 4820-4821's own scaling (`pt[0] * .4`, `Math.max(4, Math.min(104, pt[1]))`), applied to
// the same static points above instead of the live window — see this file's header.
const MOBILE_BAD_LINE_POINTS = '0,55.8 4,22.5 8.1,31.1 12.2,30.6 16.2,36.8 20.2,32.7 24.3,47.8 28.4,45.5 32.4,9.2 36.4,36.7 40.5,40.2 44.6,51.7 48.6,53.6 52.6,80.8 56.7,86.6 60.8,83.3 64.8,58 68.9,64.4 72.9,59.9 77,29.9 81,32 85.1,66.7 89.1,61.2 93.2,39.8 97.2,34.6 101.3,24.3 105.3,22.9 109.4,10.4 113.4,42.7 117.5,53.6 121.5,29.3 125.6,23.5 129.6,53.7 133.7,53.3 137.7,54.8 141.8,70.9 145.8,82.9 149.9,89.1 153.9,62.4 158,74.6 162,60.1 166.1,22.3 170.1,11 174.2,46.9 178.2,50.8 182.3,51.2 186.3,45.7 190.4,35.9 194.4,27.2 198.5,9.2 202.5,32.6 206.6,46.9 210.6,62.2 214.7,39.7 218.7,74.7 222.8,70.6 226.8,60.4 230.9,63.9 234.9,68.4 239,76 243,52 247.1,79.4 251.1,68.8 255.2,53 259.2,9.2 263.3,30.4 267.4,31.3 271.4,28.6 275.4,36.1 279.5,47.8 283.6,44.7 287.6,9.2 291.6,36.7 295.7,41.6 299.8,54.2 303.8,40.8 307.8,82.4 311.9,86.7 316,68 320,67.7';
const MOBILE_GOOD_LINE_POINTS = '0,62.8 4,55.7 8.1,54.1 12.2,56.2 16.2,59.4 20.2,61.5 24.3,62.4 28.4,63.4 32.4,66.7 36.4,73.7 40.5,84.2 44.6,95.6 48.6,101.3 52.6,101.3 56.7,99.6 60.8,85.9 64.8,68.6 68.9,52.9 72.9,46.3 77,46.3 81,47.8 85.1,58.5 89.1,69.8 93.2,78.4 97.2,83.1 101.3,84.8 105.3,85.5 109.4,87 113.4,89.9 117.5,92.8 121.5,93.1 125.6,88.4 129.6,78.1 133.7,64 137.7,49.9 141.8,46.3 145.8,46.3 149.9,46.3 153.9,60.6 158,77.3 162,91.5 166.1,99.6 170.1,100.5 174.2,95.6 178.2,88.3 182.3,81.4 186.3,76.7 190.4,74 194.4,71.6 198.5,67.5 202.5,61.1 206.6,53.6 210.6,47.8 214.7,47.1 218.7,53.4 222.8,66.3 226.8,82.8 230.9,98.1 234.9,101.3 239,101.3 243,100.6 247.1,87.2 251.1,72.6 255.2,61.2 259.2,55.1 263.3,54.3 267.4,56.8 271.4,59.8 275.4,61.8 279.5,62.5 283.6,63.7 287.6,67.6 291.6,75.3 295.7,86.1 299.8,97.4 303.8,101.3 307.8,101.3 311.9,97.7 316,83.1 320,65.7';

export function BeforeAfter() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(beforeAfterAnimation);
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  function positionFromClientX(clientX: number): number {
    const card = cardRef.current;
    if (!card) return position;
    const rect = card.getBoundingClientRect();
    const width = rect.width || 1;
    return clampPosition(((clientX - rect.left) / width) * 100);
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>): void {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
    setPosition(positionFromClientX(event.clientX));
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>): void {
    if (!isDragging) return;
    setPosition(positionFromClientX(event.clientX));
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPosition((current) => clampPosition(current + KEYBOARD_STEP_PERCENT));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPosition((current) => clampPosition(current - KEYBOARD_STEP_PERCENT));
    }
  }

  const badClipPath = `inset(0 ${(100 - position).toFixed(3)}% 0 0)`;
  const goodClipPath = `inset(0 0 0 ${position.toFixed(3)}%)`;

  return (
    <section id='before-after' ref={sectionRef} className='relative px-4 py-14 md:px-8 md:py-20' style={{ zIndex: 1 }}>
      <div className='pointer-events-none absolute inset-0' aria-hidden='true' style={{ background: 'linear-gradient(to bottom, transparent, #93FF1803, transparent)' }} />
      <div className='relative mx-auto max-w-6xl' style={{ zIndex: 1 }}>
        <div className='ba-section-head mb-10 text-center md:mb-14'>
          <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: '#2ECDFF', fontFamily: 'var(--font-jetbrains)' }}>
            {t('beforeAfter.eyebrow')}
          </span>
          <h2 className='mb-4 text-3xl font-bold md:text-5xl' style={{ fontFamily: 'var(--font-outfit)', color: '#E5E7EB' }}>
            {t('beforeAfter.title')} <span style={{ color: '#2ECDFF' }}>{t('beforeAfter.titleHighlight')}</span>
          </h2>
          <p className='mx-auto max-w-2xl text-base md:text-lg' style={{ color: '#AAB5C1', fontFamily: 'var(--font-outfit)' }}>
            {t('beforeAfter.sub')}
          </p>
        </div>
        <div data-anim='ba-slider' ref={cardRef} className={cn('ba-compare-card relative touch-none select-none overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D12]', isDragging && 'np-dragging')} style={{ height: '400px' }}>
          <div className='absolute inset-0'>
            <svg viewBox='0 0 800 120' className='absolute bottom-0 start-0 w-full' style={{ height: '45%' }} preserveAspectRatio='none'>
              <line x1='0' y1='30' x2='800' y2='30' stroke='white' strokeOpacity='0.05' strokeDasharray='4 4' />
              <line x1='0' y1='60' x2='800' y2='60' stroke='white' strokeOpacity='0.05' strokeDasharray='4 4' />
              <line x1='0' y1='90' x2='800' y2='90' stroke='white' strokeOpacity='0.05' strokeDasharray='4 4' />
              <g style={{ clipPath: badClipPath }}>
                <polyline fill='none' stroke='#FD3154' strokeWidth='2.5' strokeLinejoin='round' points={BAD_LINE_POINTS} />
                <polyline fill='#FD315415' stroke='none' points={`0,120 ${BAD_LINE_POINTS} 800,120`} />
              </g>
              <g style={{ clipPath: goodClipPath }}>
                <polyline fill='none' stroke='#93FF18' strokeWidth='2.5' strokeLinejoin='round' points={GOOD_LINE_POINTS} />
                <polyline fill='#93FF1815' stroke='none' points={`0,120 ${GOOD_LINE_POINTS} 800,120`} />
              </g>
            </svg>
          </div>
          <div className='absolute inset-0 flex flex-col p-6 md:p-8' style={{ clipPath: badClipPath }}>
            <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-[#FD3154]/10 to-transparent' />
            <div className='relative z-10'>
              <div className='mb-3 flex items-center gap-2'>
                <div data-anim='ba-live-dot' className='size-2.5 animate-pulse rounded-full bg-[#FD3154]' />
                <span className='text-xs font-semibold uppercase tracking-widest' style={{ color: '#FD3154', fontFamily: 'var(--font-jetbrains)' }}>
                  {t('beforeAfter.withoutNoping')}
                </span>
              </div>
              <div className='ba-pane-title mb-5 text-3xl font-black md:text-4xl' style={{ color: '#FD3154', fontFamily: 'var(--font-outfit)' }}>
                {t('beforeAfter.laggy')}
              </div>
              <div className='space-y-3'>
                <div className='flex items-baseline gap-3'>
                  <span className='text-xs uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-jetbrains)' }}>
                    {t('beforeAfter.avgPing')}
                  </span>
                  <span className='text-2xl font-bold' data-anim='ba-avg-bad' style={{ color: '#FD3154', fontFamily: 'var(--font-jetbrains)' }}>
                    156ms
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-sm' style={{ color: '#FD3154' }}>
                    ⚠
                  </span>
                  <span className='ba-live-detail text-sm' data-anim='ba-loss-bad' style={{ color: '#FD3154', fontFamily: 'var(--font-outfit)' }}>
                    Packet loss: 1.0%
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-sm' style={{ color: '#FD3154' }}>
                    ⚠
                  </span>
                  <span className='ba-live-detail text-sm' data-anim='ba-jitter-bad' style={{ color: '#FD3154', fontFamily: 'var(--font-outfit)' }}>
                    Jitter: 42ms
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className='absolute inset-0 flex flex-col p-6 md:p-8' style={{ clipPath: goodClipPath }}>
            <div className='pointer-events-none absolute inset-0 bg-gradient-to-bl from-[#93FF18]/5 to-transparent' />
            <div className='relative z-10 text-end'>
              <div className='mb-3 flex items-center justify-end gap-2'>
                <span className='text-xs font-semibold uppercase tracking-widest' style={{ color: '#93FF18', fontFamily: 'var(--font-jetbrains)' }}>
                  {t('beforeAfter.withNoping')}
                </span>
                <div className='size-2.5 rounded-full bg-[#93FF18]' />
              </div>
              <div className='ba-pane-title mb-5 text-3xl font-black md:text-4xl' style={{ color: '#93FF18', fontFamily: 'var(--font-outfit)' }}>
                {t('beforeAfter.smooth')}
              </div>
              <div className='space-y-3'>
                <div className='flex items-center justify-end gap-3'>
                  <span className='text-xs uppercase tracking-wider' style={{ color: '#929298', fontFamily: 'var(--font-jetbrains)' }}>
                    {t('beforeAfter.avgPing')}
                  </span>
                  <span className='text-2xl font-bold' data-anim='ba-avg-good' style={{ color: '#93FF18', fontFamily: 'var(--font-jetbrains)' }}>
                    3.0ms
                  </span>
                </div>
                <div className='flex items-center justify-end gap-2'>
                  <span className='ba-live-detail text-sm' data-anim='ba-loss-good' style={{ color: '#93FF18', fontFamily: 'var(--font-outfit)' }}>
                    Packet loss: 0.0%
                  </span>
                  <span className='text-sm' style={{ color: '#93FF18' }}>
                    ✓
                  </span>
                </div>
                <div className='flex items-center justify-end gap-2'>
                  <span className='ba-live-detail text-sm' data-anim='ba-jitter-good' style={{ color: '#93FF18', fontFamily: 'var(--font-outfit)' }}>
                    Jitter: 1.0ms
                  </span>
                  <span className='text-sm' style={{ color: '#93FF18' }}>
                    ✓
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Source markup carries no role/tabindex/aria-* at all (3674) — the script adds them
              at runtime (4711-4717), and this task's brief asks for exactly that, plus arrow-key
              support, as the deliberate accessibility improvement over the attachment: same
              divider, now reachable without a pointer. */}
          <div data-anim='ba-handle' role='slider' tabIndex={0} aria-label={HUD_STRINGS_EN.beforeAfter.comparisonDivider} aria-valuemin={MIN_POSITION} aria-valuemax={MAX_POSITION} aria-valuenow={Math.round(position)} className='absolute inset-y-0 z-30 flex cursor-col-resize items-center justify-center' style={{ left: `${position}%`, transform: 'translateX(-50%)', width: '44px' }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onKeyDown={handleKeyDown}>
            <div className='h-full w-px bg-white/40' />
            <div className='np-ba-knob absolute flex size-11 items-center justify-center rounded-full border-2 border-white/40 bg-white/10 shadow-lg backdrop-blur-md'>
              <svg width='18' height='18' viewBox='0 0 16 16' fill='none'>
                <path d='M5 3L2 8L5 13' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                <path d='M11 3L14 8L11 13' stroke='white' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </div>
          </div>
          <div className='absolute bottom-3 left-1/2 z-20 -translate-x-1/2'>
            <span className='rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-widest' style={{ color: '#929298', fontFamily: 'var(--font-jetbrains)' }}>
              {t('beforeAfter.dragHint')}
            </span>
          </div>
        </div>
        <div className='np3-mobile-ba'>
          <article className='is-bad'>
            <header>
              <span>{t('beforeAfter.withoutNoping')}</span>
              <b>
                <i>156</i>ms
              </b>
            </header>
            <svg viewBox='0 0 320 108' preserveAspectRatio='none'>
              <g>
                <path d='M0 27H320M0 54H320M0 81H320' />
                <path d='M80 0V108M160 0V108M240 0V108' />
              </g>
              <polyline points={MOBILE_BAD_LINE_POINTS} />
            </svg>
            <footer>
              <i />
              {t('beforeAfter.laggy')}
            </footer>
          </article>
          <article className='is-good'>
            <header>
              <span>{t('beforeAfter.withNoping')}</span>
              <b>
                <i>3.0</i>ms
              </b>
            </header>
            <svg viewBox='0 0 320 108' preserveAspectRatio='none'>
              <g>
                <path d='M0 27H320M0 54H320M0 81H320' />
                <path d='M80 0V108M160 0V108M240 0V108' />
              </g>
              <polyline points={MOBILE_GOOD_LINE_POINTS} />
            </svg>
            <footer>
              <i />
              {t('beforeAfter.smooth')}
            </footer>
          </article>
        </div>
      </div>
    </section>
  );
}
